import { useState, useCallback } from "react";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import {
  Loader2, Brain, AlertTriangle, CheckCircle2, XCircle,
  ArrowRight, FlaskConical, Shield, Info,
} from "lucide-react";
import { CollapsibleSection } from "./CollapsibleSection";
import ArtifactQualityReview from "../../ArtifactQualityReview";
import CreditCostTag from "../../CreditCostTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Helper functions ── */

function detectAssumptionFailures(result) {
  const failures = [];
  const ac = result.assumption_checks || {};
  if (ac.normality && ac.normality.status !== "passed" && ac.normality.status !== "not_checked") {
    failures.push({ check: "Normality", detail: ac.normality.detail || "Normality assumption violated" });
  }
  if (ac.variance_equality && ac.variance_equality.status !== "passed" && ac.variance_equality.status !== "not_checked") {
    failures.push({ check: "Equal Variance", detail: ac.variance_equality.detail || "Equal variance assumption violated" });
  }
  if (ac.expected_cell_count && (ac.expected_cell_count.status === "failed" || ac.expected_cell_count.status === "low")) {
    failures.push({ check: "Expected Cell Count", detail: "Low expected cell counts detected" });
  }
  if (ac.overdispersion && (ac.overdispersion.status === "detected" || ac.overdispersion.resolved === false)) {
    failures.push({ check: "Overdispersion", detail: ac.overdispersion.detail || "Overdispersion not resolved" });
  }
  const warnings = ac.warnings || [];
  for (const w of warnings) {
    const wl = (w || "").toLowerCase();
    if (wl.includes("normality") && !failures.some(f => f.check === "Normality")) failures.push({ check: "Normality", detail: w });
    if ((wl.includes("variance") || wl.includes("heteroscedast")) && !failures.some(f => f.check === "Equal Variance")) failures.push({ check: "Equal Variance", detail: w });
    if (wl.includes("expected cell") && !failures.some(f => f.check === "Expected Cell Count")) failures.push({ check: "Expected Cell Count", detail: w });
    if (wl.includes("overdispers") && !failures.some(f => f.check === "Overdispersion")) failures.push({ check: "Overdispersion", detail: w });
  }
  return failures;
}

function extractExcludedVariables(result, classifications) {
  const excluded = [];
  const seen = new Set();
  if (classifications) {
    for (const [col, type] of Object.entries(classifications)) {
      if (type === "time_index" && !seen.has(col)) {
        excluded.push({ variable: col, reason: "Time index variable \u2014 excluded from inferential analysis" });
        seen.add(col);
      }
    }
  }
  if (result.excluded_variables?.length > 0) {
    for (const v of result.excluded_variables) {
      const name = v.variable || v.name || String(v);
      if (!seen.has(name)) { excluded.push({ variable: name, reason: v.reason || "Excluded from analysis" }); seen.add(name); }
    }
  }
  if (result.data_limitations?.length > 0) {
    for (const lim of result.data_limitations) {
      const limLower = (lim || "").toLowerCase();
      if (limLower.includes(">20% missing") || limLower.includes("missing data")) {
        const varMatch = lim.match(/['"]([^'"]+)['"]/);
        if (varMatch && !seen.has(varMatch[1])) { excluded.push({ variable: varMatch[1], reason: ">20% missing data" }); seen.add(varMatch[1]); }
      }
      if (limLower.includes("constant") || limLower.includes("zero variance")) {
        const varMatch = lim.match(/['"]([^'"]+)['"]/);
        if (varMatch && !seen.has(varMatch[1])) { excluded.push({ variable: varMatch[1], reason: "Constant value \u2014 zero variance" }); seen.add(varMatch[1]); }
      }
    }
  }
  return excluded;
}

function deriveAiExecSummaryV2(result, stabilityStatus, decisionReadiness, assumptionFailures) {
  const hasDrivers = !!result.ranked_drivers?.primary_driver;
  const validationStatus = result.ranked_drivers?.validation_status || "no_significant_drivers";
  const isDescriptiveOnly = !hasDrivers && !result.statistical_evidence?.test_name;
  const isValidated = validationStatus === "statistically_validated";
  const r2 = result.statistical_evidence?.r_squared;
  const hasAssumptionFailures = assumptionFailures.length > 0;
  const isUnstable = stabilityStatus === "unstable";

  const drStatus = decisionReadiness?.status || "unknown";
  let drLabel, drColor;
  switch (drStatus) {
    case "action_ready":     drLabel = "Action Ready";      drColor = "text-emerald-400"; break;
    case "pilot_required":   drLabel = "Pilot Required";    drColor = "text-amber-400";   break;
    case "exploratory_only": drLabel = "Exploratory Only";  drColor = "text-amber-400";   break;
    case "blocked":          drLabel = "Blocked";           drColor = "text-red-400";     break;
    default:                 drLabel = "Not Evaluated";     drColor = "text-muted-foreground";
  }

  if (isValidated && !hasDrivers) {
    console.error("[AUDIT] Executive Consistency Violation: Root Cause Status = Validated but no significant drivers returned. Forcing fallback.");
  }

  let rcStatus, rcColor;
  if (isValidated && !hasDrivers) { rcStatus = "No Statistically Validated Drivers"; rcColor = "text-red-400"; }
  else if (isDescriptiveOnly) { rcStatus = "No Inferential Root Cause Validation Performed"; rcColor = "text-muted-foreground"; }
  else if (isUnstable) { rcStatus = hasDrivers ? "Preliminary Validated Drivers" : "Preliminary Findings"; rcColor = "text-amber-400"; }
  else if (hasAssumptionFailures) { rcStatus = hasDrivers ? "Preliminary Validated Drivers" : "Preliminary Findings"; rcColor = "text-amber-400"; }
  else if (isValidated && stabilityStatus === "stable") { rcStatus = "Validated Root Cause Identified"; rcColor = "text-emerald-400"; }
  else if (hasDrivers) { rcStatus = "Validated Root Cause Identified"; rcColor = "text-emerald-400"; }
  else { rcStatus = "No Statistically Validated Drivers"; rcColor = "text-red-400"; }

  let modelStrength, modelColor;
  if (hasAssumptionFailures) { modelStrength = "Weak Evidence"; modelColor = "text-red-400"; }
  else if (isValidated && (r2 == null || r2 >= 0.3)) { modelStrength = "Strong Evidence"; modelColor = "text-emerald-400"; }
  else if (hasDrivers && r2 != null && r2 >= 0.1) { modelStrength = "Moderate Evidence"; modelColor = "text-amber-400"; }
  else if (hasDrivers) { modelStrength = "Weak Evidence"; modelColor = "text-red-400"; }
  else { modelStrength = "No Validated Evidence"; modelColor = "text-muted-foreground"; }

  let stabilityLabel, stabilityColor;
  if (isUnstable) { stabilityLabel = "Unstable \u2014 Inference Downgraded"; stabilityColor = "text-red-400"; }
  else if (stabilityStatus === "stable") { stabilityLabel = "Stable"; stabilityColor = "text-emerald-400"; }
  else { stabilityLabel = "Unknown \u2014 Pilot Required"; stabilityColor = "text-amber-400"; }

  let nextAction;
  if (drStatus === "blocked") nextAction = "Improve phase blocked \u2014 address governance gaps first.";
  else if (drStatus === "pilot_required") nextAction = "Pilot required before implementation.";
  else if (drStatus === "exploratory_only") nextAction = "Findings exploratory \u2014 additional validation required.";
  else if (drStatus === "action_ready") { nextAction = isUnstable ? "Stabilize process before implementing solutions." : "Proceed to Improve Phase."; }
  else if (isUnstable) nextAction = "Stabilize process before implementing solutions.";
  else if (isDescriptiveOnly) nextAction = "No inferential root cause validation performed.";
  else if (!hasDrivers) nextAction = "Increase sample size, refine measurement system, or introduce additional candidate predictors.";
  else nextAction = "Review findings and assess evidence strength.";

  return { rcStatus, rcColor, modelStrength, modelColor, stabilityLabel, stabilityColor, drLabel, drColor, nextAction, hasNoDrivers: !hasDrivers };
}

function getAiConfidenceColor(level) {
  if (level === "High") return "bg-emerald-500/10 text-emerald-400 border-emerald-500/30";
  if (level === "Medium" || level === "Moderate") return "bg-amber-500/10 text-amber-400 border-amber-500/30";
  return "bg-red-500/10 text-red-400 border-red-500/30";
}

function AiEffectBadge({ driver }) {
  if (!driver?.strength) return null;
  const pStr = String(driver.statistical_significance || "");
  const pVal = parseFloat(pStr);
  const isUnreliable = isNaN(pVal) || pVal >= 0.05;
  if (isUnreliable) return <span className="px-2 py-0.5 text-[10px] rounded bg-muted/50 text-muted-foreground border border-border" title="Effect magnitude suppressed because statistical significance was not met." data-testid="ai-effect-unreliable">Not Reliable</span>;
  const strength = driver.strength?.toLowerCase();
  const cls = strength === "strong" ? "bg-emerald-500/15 text-emerald-400" : strength === "moderate" ? "bg-amber-500/15 text-amber-400" : "bg-muted text-muted-foreground";
  return <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${cls}`}>{driver.strength}</span>;
}

function AiEffectSizeCard({ effectSize, pValue, alpha }) {
  const isUnreliable = pValue == null || pValue >= (alpha || 0.05);
  if (isUnreliable) return (
    <div className="bg-muted/30 border border-border rounded-lg p-3 opacity-60" title="Effect magnitude suppressed because statistical significance was not met." data-testid="ai-effect-size-unreliable">
      <p className="text-xs text-muted-foreground">Effect Size</p>
      <p className="font-semibold text-muted-foreground">Not Reliable</p>
    </div>
  );
  return (
    <div className="bg-background border border-border rounded-lg p-3">
      <p className="text-xs text-muted-foreground">Effect Size</p>
      <p className="font-semibold">{effectSize.interpretation}</p>
    </div>
  );
}

/* ── AI Results View ── */

function AiResultsView({ result, stabilityStatus, decisionReadiness, classifications, datasetName, targetColumn }) {
  const [driversOpen, setDriversOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);
  const [excludedOpen, setExcludedOpen] = useState(false);
  const [assumptionsUsedOpen, setAssumptionsUsedOpen] = useState(false);
  const [methodOpen, setMethodOpen] = useState(false);
  const [metadataOpen, setMetadataOpen] = useState(false);

  const hasDrivers = !!result.ranked_drivers?.primary_driver;
  const isDescriptiveOnly = !hasDrivers && !result.statistical_evidence?.test_name;
  const assumptionFailures = detectAssumptionFailures(result);
  const summary = deriveAiExecSummaryV2(result, stabilityStatus, decisionReadiness, assumptionFailures);
  const excludedVars = extractExcludedVariables(result, classifications);
  const alpha = result.statistical_evidence?.alpha || 0.05;
  const n = result.assumption_checks?.sample_size?.n;
  const ivCount = (result.ranked_drivers?.primary_driver ? 1 : 0) + (result.ranked_drivers?.secondary_drivers?.length || 0) + (result.ranked_drivers?.non_significant_factors?.length || 0);
  const analysisTimestamp = new Date().toISOString();

  return (
    <div className="space-y-4 mt-6" data-testid="ai-results-view">
      {/* 1. Executive Summary */}
      <div className="bg-card border-2 border-primary/20 rounded-lg p-5" data-testid="ai-executive-summary">
        <h4 className="text-xs font-bold uppercase tracking-wider text-primary mb-4">AI Root Cause \u2014 Analysis Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
          <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Root Cause Status</p><p className={`text-sm font-semibold ${summary.rcColor}`} data-testid="ai-exec-rc-status">{summary.rcStatus}</p></div>
          <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Model Strength</p><p className={`text-sm font-semibold ${summary.modelColor}`} data-testid="ai-exec-model-strength">{summary.modelStrength}</p></div>
          <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Process Stability</p><p className={`text-sm font-semibold ${summary.stabilityColor}`} data-testid="ai-exec-stability">{summary.stabilityLabel}</p></div>
          <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Decision Readiness</p><p className={`text-sm font-semibold ${summary.drColor}`} data-testid="ai-exec-decision-readiness">{summary.drLabel}</p></div>
          <div className="md:col-span-2"><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Recommended Next Action</p><p className="text-sm font-semibold text-foreground" data-testid="ai-exec-next-action">{summary.nextAction}</p></div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {stabilityStatus === "unstable" && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[10px] font-medium" data-testid="ai-unstable-badge"><AlertTriangle className="w-3 h-3" /> Findings Preliminary \u2014 Process Unstable</span>}
          {assumptionFailures.length > 0 && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] font-medium" data-testid="ai-assumption-violations-badge"><AlertTriangle className="w-3 h-3" /> Assumption Violations Detected</span>}
        </div>
      </div>

      {/* 3. Ranked Drivers */}
      <CollapsibleSection title={<>Ranked Drivers {!hasDrivers && <span className="text-xs text-muted-foreground ml-1">(none identified)</span>}</>} icon={<ArrowRight className="w-4 h-4 text-primary" />} open={driversOpen} onToggle={() => setDriversOpen(v => !v)} testId="ai-ranked-drivers">
        {hasDrivers ? (
          <div className="space-y-3">
            <div className={`p-4 rounded-lg ${stabilityStatus === "unstable" ? "bg-amber-500/5 border border-dashed border-amber-500/30" : "bg-emerald-500/5 border border-emerald-500/30"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${stabilityStatus === "unstable" ? "bg-amber-500/20 text-amber-400" : "bg-emerald-500/20 text-emerald-400"}`}>PRIMARY</span>
                {stabilityStatus === "unstable" && <span className="px-2 py-0.5 text-[9px] rounded bg-amber-500/10 text-amber-400">Preliminary</span>}
                <AiEffectBadge driver={result.ranked_drivers.primary_driver} />
              </div>
              <p className="font-semibold text-lg">{result.ranked_drivers.primary_driver.variable}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2 text-sm">
                <div><span className="text-muted-foreground">Direction:</span> <span className="font-medium ml-1">{result.ranked_drivers.primary_driver.direction}</span></div>
                <div><span className="text-muted-foreground">Impact:</span> <span className="font-medium ml-1">{result.ranked_drivers.primary_driver.impact_magnitude?.toFixed(3)}</span></div>
                <div><span className="text-muted-foreground">P-value:</span> <span className="font-medium ml-1">{result.ranked_drivers.primary_driver.statistical_significance}</span></div>
                <div><span className="text-muted-foreground">Status:</span> <span className="font-medium ml-1 text-emerald-400">{result.ranked_drivers.primary_driver.confidence}</span></div>
              </div>
            </div>
            {result.ranked_drivers.secondary_drivers?.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Secondary Drivers:</p>
                {result.ranked_drivers.secondary_drivers.map((driver, i) => (
                  <div key={i} className="p-3 bg-muted/30 border border-border rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs font-medium rounded">#{i + 2}</span>
                      <span className="font-medium">{driver.variable}</span>
                      <AiEffectBadge driver={driver} />
                    </div>
                    <div className="text-sm text-muted-foreground">{driver.direction} | p={driver.statistical_significance}</div>
                  </div>
                ))}
              </div>
            )}
            {result.ranked_drivers.non_significant_factors?.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-muted-foreground mb-2">Non-Significant Factors:</p>
                <div className="flex flex-wrap gap-2">
                  {result.ranked_drivers.non_significant_factors.map((f, i) => <span key={i} className="px-3 py-1 bg-muted/40 text-muted-foreground text-sm rounded-full">{f.variable} (p={f.p_value})</span>)}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6">
            <XCircle className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="font-medium" data-testid="ai-no-drivers-msg">{isDescriptiveOnly ? "No inferential root cause validation performed" : "No statistically validated drivers identified"}</p>
            <p className="text-sm text-muted-foreground mt-1">Increase sample size, refine measurement system, or introduce additional candidate predictors.</p>
          </div>
        )}
      </CollapsibleSection>

      {/* 4. Statistical Evidence */}
      <CollapsibleSection title="Statistical Evidence" icon={<FlaskConical className="w-4 h-4 text-primary" />} open={evidenceOpen} onToggle={() => setEvidenceOpen(v => !v)} testId="ai-stat-evidence">
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-background border border-border rounded-lg p-3"><p className="text-xs text-muted-foreground">Test Used</p><p className="font-semibold">{result.statistical_evidence?.test_name || "N/A"}</p></div>
          <div className="bg-background border border-border rounded-lg p-3" data-testid="ai-alpha-display"><p className="text-xs text-muted-foreground">Significance Level ({"\u03B1"})</p><p className="font-semibold font-mono">{"\u03B1"} = {alpha}</p></div>
          {result.statistical_evidence?.p_value !== undefined && (
            <div className="bg-background border border-border rounded-lg p-3"><p className="text-xs text-muted-foreground">P-Value</p><p className={`font-semibold ${result.statistical_evidence.p_value < alpha ? "text-emerald-400" : "text-muted-foreground"}`}>{result.statistical_evidence.p_value}</p></div>
          )}
          {result.statistical_evidence?.r_squared !== undefined && (
            <div className="bg-background border border-border rounded-lg p-3"><p className="text-xs text-muted-foreground">R² (Variance Explained)</p><p className="font-semibold">{(result.statistical_evidence.r_squared * 100).toFixed(1)}%</p></div>
          )}
          {result.statistical_evidence?.effect_size && <AiEffectSizeCard effectSize={result.statistical_evidence.effect_size} pValue={result.statistical_evidence?.p_value} alpha={alpha} />}
        </div>
        {result.statistical_evidence?.coefficients?.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium mb-2">Regression Coefficients:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ai-coefficients-table">
                <thead><tr className="border-b border-border text-muted-foreground"><th className="text-left py-2 px-3">Variable</th><th className="text-right py-2 px-3">Coefficient</th><th className="text-right py-2 px-3">Std. Coef.</th><th className="text-right py-2 px-3">P-Value</th><th className="text-center py-2 px-3">Significant</th></tr></thead>
                <tbody>{result.statistical_evidence.coefficients.map((c, i) => (
                  <tr key={i} className="border-b border-border/50"><td className="py-2 px-3 font-medium">{c.variable}</td><td className="py-2 px-3 text-right font-mono">{c.coefficient}</td><td className="py-2 px-3 text-right font-mono">{c.std_coefficient}</td><td className="py-2 px-3 text-right font-mono">{c.p_value}</td><td className="py-2 px-3 text-center">{c.significant ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /> : <XCircle className="w-4 h-4 text-muted-foreground mx-auto" />}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
      </CollapsibleSection>

      {/* 5. Assumption Validation */}
      <CollapsibleSection title={<>Assumption Validation {assumptionFailures.length > 0 && <span className="text-[10px] text-red-400 ml-1">({assumptionFailures.length} issue{assumptionFailures.length > 1 ? "s" : ""})</span>}</>} icon={<Shield className="w-4 h-4 text-primary" />} open={assumptionsOpen} onToggle={() => setAssumptionsOpen(v => !v)} testId="ai-assumptions">
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-background border border-border rounded-lg p-3"><p className="text-xs text-muted-foreground">Sample Size</p><p className={`font-semibold ${result.assumption_checks?.sample_size?.status === "adequate" ? "text-emerald-400" : result.assumption_checks?.sample_size?.status === "moderate" ? "text-amber-400" : "text-red-400"}`}>n = {result.assumption_checks?.sample_size?.n} ({result.assumption_checks?.sample_size?.status})</p></div>
          {result.assumption_checks?.normality?.status !== "not_checked" && <div className="bg-background border border-border rounded-lg p-3"><p className="text-xs text-muted-foreground">Normality</p><p className={`font-semibold ${result.assumption_checks?.normality?.status === "passed" ? "text-emerald-400" : "text-amber-400"}`}>{result.assumption_checks?.normality?.status}</p></div>}
          {result.assumption_checks?.variance_equality?.status !== "not_checked" && <div className="bg-background border border-border rounded-lg p-3"><p className="text-xs text-muted-foreground">Equal Variance</p><p className={`font-semibold ${result.assumption_checks?.variance_equality?.status === "passed" ? "text-emerald-400" : "text-amber-400"}`}>{result.assumption_checks?.variance_equality?.status}</p></div>}
          {result.assumption_checks?.multicollinearity?.status !== "not_checked" && <div className="bg-background border border-border rounded-lg p-3"><p className="text-xs text-muted-foreground">Multicollinearity</p><p className={`font-semibold ${result.assumption_checks?.multicollinearity?.status === "acceptable" ? "text-emerald-400" : "text-amber-400"}`}>{result.assumption_checks?.multicollinearity?.status}</p></div>}
        </div>
        {result.assumption_checks?.warnings?.length > 0 && (
          <div className="mt-3 space-y-2">{result.assumption_checks.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 text-sm p-2 bg-amber-500/10 border border-amber-500/20 rounded"><AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" /><span className="text-amber-200">{w}</span></div>
          ))}</div>
        )}
      </CollapsibleSection>

      {/* 6. Excluded Variables */}
      {excludedVars.length > 0 && (
        <CollapsibleSection title={`Excluded Variables (${excludedVars.length})`} open={excludedOpen} onToggle={() => setExcludedOpen(v => !v)} testId="ai-excluded-vars">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ai-excluded-table">
              <thead><tr className="border-b border-border text-muted-foreground"><th className="text-left py-2 px-3 font-medium">Variable</th><th className="text-left py-2 px-3 font-medium">Reason</th></tr></thead>
              <tbody>{excludedVars.map((v, i) => <tr key={i} className="border-b border-border/50"><td className="py-2 px-3 font-medium">{v.variable}</td><td className="py-2 px-3 text-muted-foreground">{v.reason}</td></tr>)}</tbody>
            </table>
          </div>
          {result.data_limitations?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Data Limitations</p>
              <ul className="space-y-1">{result.data_limitations.map((lim, i) => <li key={i} className="text-sm text-muted-foreground flex items-start gap-2"><span className="text-amber-400">-</span> {lim}</li>)}</ul>
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* 7. Assumptions Used */}
      {result.assumptions_used?.length > 0 && (
        <CollapsibleSection title="Assumptions Used" icon={<Info className="w-4 h-4 text-primary" />} open={assumptionsUsedOpen} onToggle={() => setAssumptionsUsedOpen(v => !v)} testId="ai-assumptions-used">
          <ul className="space-y-1">{result.assumptions_used.map((a, i) => <li key={i} className="text-sm text-muted-foreground flex items-start gap-2"><span className="text-primary">-</span> {a.assumption || a}</li>)}</ul>
        </CollapsibleSection>
      )}

      {/* 8. Statistical Method Details */}
      <CollapsibleSection title="Statistical Method Details" open={methodOpen} onToggle={() => setMethodOpen(v => !v)} testId="ai-method-details">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className={`px-3 py-1.5 rounded-md border text-sm font-medium ${getAiConfidenceColor(result.confidence_level)}`} title="Confidence in statistical method selection and assumption checks" data-testid="ai-method-confidence">Method Confidence: {result.confidence_level}</div>
            <div className="px-3 py-1.5 rounded-md bg-primary/10 text-primary border border-primary/20 text-sm">{result.selected_method?.method_name || "Unknown"}</div>
          </div>
          <p className="text-xs text-muted-foreground italic">Method Confidence reflects statistical method suitability and assumption integrity \u2014 not root cause certainty.</p>
          {result.selected_method?.rationale && <div className="p-3 bg-muted/30 border border-border rounded-lg"><p className="text-sm text-muted-foreground">{result.selected_method.rationale}</p></div>}
        </div>
      </CollapsibleSection>

      {/* 9. Analysis Metadata */}
      <CollapsibleSection title="Analysis Metadata" open={metadataOpen} onToggle={() => setMetadataOpen(v => !v)} testId="ai-analysis-metadata">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm" data-testid="ai-metadata-grid">
          <div><span className="text-muted-foreground">Dataset:</span> <span className="font-medium ml-1">{datasetName || "N/A"}</span></div>
          <div><span className="text-muted-foreground">DV Selected:</span> <span className="font-medium ml-1">{targetColumn || result.business_interpretation?.target_variable || "Auto-detected"}</span></div>
          <div><span className="text-muted-foreground">IV Count Tested:</span> <span className="font-medium ml-1">{ivCount}</span></div>
          <div><span className="text-muted-foreground">Sample Size:</span> <span className="font-medium ml-1">n = {n || "N/A"}</span></div>
          <div><span className="text-muted-foreground">Method Selected:</span> <span className="font-medium ml-1">{result.selected_method?.method_name || "N/A"}</span></div>
          <div><span className="text-muted-foreground">Timestamp:</span> <span className="font-mono text-muted-foreground ml-1">{analysisTimestamp.split("T")[0]}</span></div>
          <div><span className="text-muted-foreground">Engine Version:</span> <span className="font-mono text-muted-foreground ml-1">v2.1.0-hardened</span></div>
        </div>
      </CollapsibleSection>

      {/* 10. AI Quality Review */}
      {(result.business_interpretation?.actionable_insights?.length > 0 || result.statistical_evidence?.causation_warning) && (
        <div className="bg-card border border-border rounded-lg p-4" data-testid="ai-quality-review">
          <h4 className="text-xs font-bold uppercase tracking-wider text-primary mb-3">AI Quality Review</h4>
          {result.business_interpretation?.actionable_insights?.length > 0 && (
            <ul className="space-y-1.5 mb-3">{result.business_interpretation.actionable_insights.map((insight, i) => <li key={i} className="flex items-start gap-2 text-sm"><ArrowRight className="w-4 h-4 text-primary shrink-0 mt-0.5" />{insight}</li>)}</ul>
          )}
          {result.statistical_evidence?.causation_warning && !summary.hasNoDrivers && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg" data-testid="ai-causation-warning"><p className="text-xs text-red-400 font-medium">{result.statistical_evidence.causation_warning}</p></div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main Tab Component ── */

export default function AiRootCauseTab({ projectId, token, datasets, selectedDataset, setSelectedDataset, rcaResult, decisionReadiness, getNumericColumns, initialAiResult }) {
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState(initialAiResult || null);
  const [aiTargetColumn, setAiTargetColumn] = useState("");

  const isAiTargetTimeIndex = aiTargetColumn && rcaResult?.classifications?.[aiTargetColumn] === "time_index";

  const runAIRootCauseAnalysis = useCallback(async () => {
    if (!selectedDataset) { toast.error("Please select a dataset first"); return; }
    setAiAnalyzing(true);
    setAiAnalysisResult(null);
    try {
      const dataRes = await fetch(`${API}/projects/${projectId}/datasets/${selectedDataset.dataset_id}/records`, { headers: { Authorization: `Bearer ${token}` } });
      if (!dataRes.ok) throw new Error("Failed to fetch dataset records");
      const records = await dataRes.json();
      if (!records || records.length === 0) throw new Error("Dataset has no records");

      const response = await fetch(`${API}/ai/autonomous-analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ data: records, target_column: aiTargetColumn || null, project_id: projectId, dataset_id: selectedDataset?.dataset_id }),
      });
      if (!response.ok) { const error = await response.json(); throw new Error(error.detail || "AI analysis failed"); }
      const result = await response.json();
      setAiAnalysisResult(result);
      toast.success("AI Root Cause Analysis complete");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAiAnalyzing(false);
    }
  }, [projectId, token, selectedDataset, aiTargetColumn]);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="font-heading font-semibold flex items-center gap-2"><Brain className="w-5 h-5 text-primary" />AI Autonomous Root Cause Analysis</h3>
          <p className="text-sm text-muted-foreground mt-1">The AI automatically selects the appropriate statistical method based on your data structure</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="space-y-2">
          <Label>Dataset</Label>
          <select value={selectedDataset?.dataset_id || ""} onChange={(e) => { const ds = datasets.find((d) => d.dataset_id === e.target.value); setSelectedDataset(ds); setAiTargetColumn(""); setAiAnalysisResult(null); }} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="ai-dataset-select">
            <option value="">Select dataset</option>
            {datasets.map((ds) => <option key={ds.dataset_id} value={ds.dataset_id}>{ds.filename}</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <Label>Target Column (Optional)</Label>
          <select value={aiTargetColumn} onChange={(e) => setAiTargetColumn(e.target.value)} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" disabled={!selectedDataset} data-testid="ai-target-select">
            <option value="">Auto-detect</option>
            {getNumericColumns().map((col) => <option key={col} value={col}>{col}</option>)}
          </select>
          <p className="text-xs text-muted-foreground">The outcome variable you want to explain (e.g., defects, cycle time)</p>
        </div>
        <div className="flex items-end">
          <Button onClick={runAIRootCauseAnalysis} disabled={!selectedDataset || aiAnalyzing || isAiTargetTimeIndex} className="w-full gap-2" size="lg" data-testid="run-ai-analysis-btn">
            {aiAnalyzing ? (<><Loader2 className="w-4 h-4 animate-spin" />Analyzing...</>) : (<><Brain className="w-4 h-4" />Run AI Root Cause Analysis</>)}
            <CreditCostTag cost={10} />
          </Button>
        </div>
      </div>

      {isAiTargetTimeIndex && (
        <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg mb-4" data-testid="ai-time-index-block">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
          <p className="text-sm text-red-400">Time-index variables cannot be used as dependent variables for inferential root cause validation.</p>
        </div>
      )}

      {aiAnalysisResult && (
        <AiResultsView result={aiAnalysisResult} stabilityStatus={rcaResult?.guardrails?.measure_preconditions?.stability_status || "unknown"} decisionReadiness={decisionReadiness} classifications={rcaResult?.classifications} datasetName={selectedDataset?.filename} targetColumn={aiTargetColumn} />
      )}

      {!aiAnalysisResult && !aiAnalyzing && (
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <Brain className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-2">Select a dataset and click "Run AI Root Cause Analysis"</p>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">The AI will automatically detect variable types, select the appropriate statistical method, validate assumptions, rank drivers, and provide business-level interpretation.</p>
        </div>
      )}

      {aiAnalysisResult && <ArtifactQualityReview projectId={projectId} artifactType="root_cause_analysis" token={token} className="mt-6" />}
    </div>
  );
}
